import { motion } from 'framer-motion';
import { Phone, Route, LifeBuoy } from 'lucide-react';
import { MANIFESTO } from '@/constants/testIds';

const STEPS = [
	{
		n: '01',
		icon: Phone,
		title: 'Call.',
		copy: 'One number, real humans. Tell us where you are and what happened — no forms, no bots, no waiting on hold.',
	},
	{
		n: '02',
		icon: Route,
		title: 'Get Matched.',
		copy: 'We dispatch the closest licensed operator to your exact GPS location within minutes.',
	},
	{
		n: '03',
		icon: LifeBuoy,
		title: 'Help Arrives.',
		copy: 'You get an ETA in real time. Your operator arrives, resolves the issue, and gets you moving again.',
	},
];

export const Manifesto = () => {
	return (
		<section
			id="how"
			data-testid={MANIFESTO.root}
			className="relative bg-brand-navy text-white overflow-hidden"
		>
			<div aria-hidden className="absolute inset-0 grain" />

			<div className="relative mx-auto max-w-[1400px] px-4 sm:px-8 py-24 sm:py-32">
				<div className="grid md:grid-cols-12 gap-6 mb-16 sm:mb-24">
					<div className="md:col-span-4">
						<div className="font-chivo text-xs uppercase tracking-[0.3em] text-brand-red font-bold mb-4">
							/ How It Works
						</div>
					</div>
					<div className="md:col-span-8">
						<motion.h2
							initial={{ opacity: 0, y: 30 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, margin: '-60px' }}
							transition={{ duration: 0.7 }}
							className="font-anton text-[40px] sm:text-[72px] md:text-[92px] leading-[0.92]"
						>
							A three-step manifesto for{' '}
							<span className="text-brand-red">
								getting unstuck.
							</span>
						</motion.h2>
					</div>
				</div>

				<div className="grid md:grid-cols-3 gap-0 md:gap-0 border-t border-white/20">
					{STEPS.map((s, i) => (
						<motion.div
							key={s.n}
							data-testid={MANIFESTO.step(s.n)}
							initial={{ opacity: 0, y: 40 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, margin: '-80px' }}
							transition={{ duration: 0.7, delay: i * 0.12 }}
							className="relative pt-8 sm:pt-12 pb-8 sm:pb-12 px-2 sm:px-6 border-t md:border-t-0 md:border-l border-white/20 first:border-l-0 first:border-t-0"
						>
							{/* Massive numeral */}
							<div
								aria-hidden
								className="absolute -top-6 sm:-top-10 left-0 font-anton text-[140px] sm:text-[220px] md:text-[240px] leading-none text-brand-red/15 select-none pointer-events-none"
							>
								{s.n}
							</div>

							<div className="relative">
								<div className="flex items-center gap-4">
									<div className="w-11 h-11 border border-white/30 flex items-center justify-center">
										<s.icon
											className="w-5 h-5"
											strokeWidth={2}
										/>
									</div>
									<span className="font-chivo text-xs uppercase tracking-[0.3em] font-bold text-brand-red">
										Step {s.n}
									</span>
								</div>

								<h3 className="mt-10 sm:mt-14 font-anton text-5xl sm:text-6xl md:text-7xl leading-none">
									{s.title}
								</h3>

								<p className="mt-6 font-plex text-white/70 text-[15px] sm:text-base leading-relaxed max-w-md">
									{s.copy}
								</p>
							</div>
						</motion.div>
					))}
				</div>

				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.6, delay: 0.2 }}
					className="mt-16 sm:mt-20 flex flex-col sm:flex-row items-start sm:items-center gap-6 border-t border-white/20 pt-10"
				>
					<div className="font-chivo text-xs uppercase tracking-[0.3em] font-bold text-white/60">
						One number. Any emergency.
					</div>
					<a
						href="tel:+13072224082"
						className="btn-red text-lg sm:text-xl"
					>
						<Phone className="w-5 h-5" strokeWidth={2.5} />
						Call (307) 222-4082
					</a>
				</motion.div>
			</div>
		</section>
	);
};

export default Manifesto;
